import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import supportTicketService from '../../../services/supportTicketService';

const TicketDetailsModal = ({ ticket, onClose, onRefresh }) => {
  const [comments, setComments] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    loadTicketDetails();
  }, [ticket?.id]);

  const loadTicketDetails = async () => {
    try {
      setLoading(true);
      const [commentsData, escalationsData, activityData] = await Promise.all([
        supportTicketService?.getTicketComments(ticket?.id),
        supportTicketService?.getTicketEscalations(ticket?.id),
        supportTicketService?.getActivityLog(ticket?.id)
      ]);
      setComments(commentsData);
      setEscalations(escalationsData);
      setActivityLog(activityData);
    } catch (error) {
      console.error('Error loading ticket details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment?.trim()) return;

    try {
      await supportTicketService?.addTicketComment({
        ticketId: ticket?.id,
        commentType: 'internal',
        authorName: 'Support Agent',
        authorEmail: 'agent@support.com',
        authorRole: 'agent',
        commentText: newComment,
        isInternal: false
      });
      setNewComment('');
      await loadTicketDetails();
      await onRefresh?.();
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'details', label: 'Details', icon: 'FileText' },
    { id: 'comments', label: 'Comments', icon: 'MessageSquare', count: comments?.length },
    { id: 'escalations', label: 'Escalations', icon: 'AlertTriangle', count: escalations?.length },
    { id: 'activity', label: 'Activity Log', icon: 'Activity', count: activityLog?.length }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{ticket?.subject}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {ticket?.ticketNumber} • {ticket?.companyName} • {ticket?.clientContactName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border px-6">
          <div className="flex gap-6">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
                {tab?.count !== undefined && (
                  <span className="bg-muted px-2 py-0.5 rounded-full text-xs">
                    {tab?.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          ) : (
            <>
              {activeTab === 'details' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Category</label>
                      <p className="text-foreground mt-1">
                        {ticket?.category?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Priority</label>
                      <p className="text-foreground mt-1">
                        {ticket?.priority?.replace(/\b\w/g, l => l?.toUpperCase())}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Status</label>
                      <p className="text-foreground mt-1">
                        {ticket?.ticketStatus?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">SLA Status</label>
                      <p className="text-foreground mt-1">
                        {ticket?.slaStatus?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Assigned Agent</label>
                      <p className="text-foreground mt-1">{ticket?.assignedAgent || 'Unassigned'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Assigned Team</label>
                      <p className="text-foreground mt-1">{ticket?.assignedTeam || 'Unassigned'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Created</label>
                      <p className="text-foreground mt-1">{formatDateTime(ticket?.createdAt)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Resolution Due</label>
                      <p className="text-foreground mt-1">{formatDateTime(ticket?.resolutionDueAt)}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                    <p className="text-foreground mt-2 whitespace-pre-wrap">{ticket?.description}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Client Contact</label>
                    <div className="mt-2 space-y-1">
                      <p className="text-foreground">{ticket?.clientContactName}</p>
                      <p className="text-sm text-muted-foreground">{ticket?.clientContactEmail}</p>
                      {ticket?.clientContactPhone && (
                        <p className="text-sm text-muted-foreground">{ticket?.clientContactPhone}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'comments' && (
                <div className="space-y-4">
                  {comments?.map(comment => (
                    <div key={comment?.id} className="bg-muted/50 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-foreground">{comment?.authorName}</p>
                          <p className="text-xs text-muted-foreground">{comment?.authorRole}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">{formatDateTime(comment?.createdAt)}</p>
                      </div>
                      <p className="text-foreground whitespace-pre-wrap">{comment?.commentText}</p>
                      {comment?.isInternal && (
                        <span className="inline-block mt-2 px-2 py-1 bg-amber-500/10 text-amber-600 text-xs rounded">
                          Internal Note
                        </span>
                      )}
                    </div>
                  ))}
                  <div className="mt-4">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e?.target?.value)}
                      placeholder="Add a comment..."
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground min-h-[100px]"
                    />
                    <Button onClick={handleAddComment} className="mt-2">
                      <Icon name="Send" size={16} className="mr-2" />
                      Add Comment
                    </Button>
                  </div>
                </div>
              )}

              {activeTab === 'escalations' && (
                <div className="space-y-4">
                  {escalations?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No escalations recorded</p>
                  ) : (
                    escalations?.map(escalation => (
                      <div key={escalation?.id} className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-foreground">Level {escalation?.escalationLevel} Escalation</p>
                            <p className="text-sm text-muted-foreground">
                              From: {escalation?.escalatedFrom || 'System'} → To: {escalation?.escalatedTo}
                            </p>
                          </div>
                          <p className="text-xs text-muted-foreground">{formatDateTime(escalation?.escalatedAt)}</p>
                        </div>
                        <p className="text-foreground">{escalation?.escalationReason}</p>
                        {escalation?.resolvedAt && (
                          <div className="mt-2 pt-2 border-t border-border">
                            <p className="text-sm text-green-600">Resolved: {formatDateTime(escalation?.resolvedAt)}</p>
                            {escalation?.resolutionNotes && (
                              <p className="text-sm text-muted-foreground mt-1">{escalation?.resolutionNotes}</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'activity' && (
                <div className="space-y-3">
                  {activityLog?.map(activity => (
                    <div key={activity?.id} className="flex items-start gap-3 pb-3 border-b border-border last:border-0">
                      <div className="bg-primary/10 p-2 rounded-full">
                        <Icon name="Activity" size={14} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{activity?.activityDescription}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {activity?.performedBy} • {formatDateTime(activity?.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailsModal;